import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  DollarSign, Tag, Percent, ToggleRight, Plus, Save, Trash2, Calendar
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PricingOffers({ token }) {
  const [pricing, setPricing] = useState({ annual_price: 199, discounts_enabled: true, promo_codes_enabled: true });
  const [promoCodes, setPromoCodes] = useState({ active: [], expired: [], inactive: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCode, setNewCode] = useState({ 
    code: "", discount_type: "percentage", discount_value: "", 
    applies_to: "annual", expiration_date: "", usage_limit: "" 
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchPricing = async () => {
    try {
      const [pricingRes, codesRes] = await Promise.all([
        axios.get(`${API}/admin/pricing`, { headers }),
        axios.get(`${API}/admin/promo-codes`, { headers })
      ]);
      setPricing(pricingRes.data);
      setPromoCodes(codesRes.data);
    } catch (error) {
      toast.error("Failed to load pricing data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPricing(); }, []);

  const savePricing = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/pricing`, pricing, { headers });
      toast.success("Pricing updated");
    } catch (error) {
      toast.error("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const createPromoCode = async () => {
    if (!newCode.code || !newCode.discount_value) { 
      toast.error("Fill required fields"); 
      return; 
    }
    try {
      await axios.post(`${API}/admin/promo-codes`, { 
        ...newCode, 
        discount_value: parseFloat(newCode.discount_value), 
        usage_limit: newCode.usage_limit ? parseInt(newCode.usage_limit) : null, 
        expiration_date: newCode.expiration_date || null 
      }, { headers });
      toast.success("Promo code created");
      setNewCode({ code: "", discount_type: "percentage", discount_value: "", applies_to: "annual", expiration_date: "", usage_limit: "" });
      fetchPricing();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed");
    }
  };

  const toggleCode = async (codeId) => {
    try { 
      await axios.post(`${API}/admin/promo-codes/${codeId}/toggle`, {}, { headers }); 
      toast.success("Updated"); 
      fetchPricing(); 
    } catch { 
      toast.error("Failed"); 
    }
  };

  const deleteCode = async (codeId) => {
    if (!window.confirm("Delete?")) return;
    try { 
      await axios.delete(`${API}/admin/promo-codes/${codeId}`, { headers }); 
      toast.success("Deleted"); 
      fetchPricing(); 
    } catch { 
      toast.error("Failed"); 
    }
  };

  if (loading) return <div className="text-center text-white/50 py-12">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="pricing-offers">
      {/* Base Price Control */}
      <div className="card p-6 border border-white/10">
        <div className="flex items-center gap-3 mb-6">
          <DollarSign className="w-5 h-5 text-[#D4AF37]" />
          <h3 className="font-['Playfair_Display'] text-lg font-bold text-white">Base Price Control</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-white/70 text-sm mb-2">Annual Plan Price ($)</label>
            <Input 
              type="number" 
              value={pricing.annual_price} 
              onChange={(e) => setPricing({...pricing, annual_price: parseFloat(e.target.value) || 0})} 
              className="input-dark"
              data-testid="annual-price-input"
            />
          </div>
          <div className="flex items-end">
            <Button onClick={savePricing} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-price-btn">
              <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Price"}
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Toggles */}
      <div className="card p-6 border border-[#00D4FF]/30 bg-gradient-to-br from-[#00D4FF]/5 to-transparent">
        <div className="flex items-center gap-3 mb-6">
          <ToggleRight className="w-5 h-5 text-[#00D4FF]" />
          <h3 className="font-['Playfair_Display'] text-lg font-bold text-white">Quick Toggles</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center justify-between p-4 bg-black/30 border border-white/10">
            <div>
              <p className="font-semibold text-white">Enable Discounts</p>
              <p className="text-white/50 text-sm">Allow discounts</p>
            </div>
            <Switch 
              checked={pricing.discounts_enabled} 
              onCheckedChange={(checked) => { 
                setPricing({...pricing, discounts_enabled: checked}); 
                axios.post(`${API}/admin/pricing`, {...pricing, discounts_enabled: checked}, { headers }); 
              }}
              data-testid="toggle-discounts"
            />
          </div>
          <div className="flex items-center justify-between p-4 bg-black/30 border border-white/10">
            <div>
              <p className="font-semibold text-white">Enable Promo Codes</p>
              <p className="text-white/50 text-sm">Allow promo codes</p>
            </div>
            <Switch 
              checked={pricing.promo_codes_enabled} 
              onCheckedChange={(checked) => { 
                setPricing({...pricing, promo_codes_enabled: checked}); 
                axios.post(`${API}/admin/pricing`, {...pricing, promo_codes_enabled: checked}, { headers }); 
              }}
              data-testid="toggle-promo-codes"
            />
          </div>
        </div>
      </div>

      {/* Create Promo Code */}
      <div className="card p-6 border border-white/10">
        <div className="flex items-center gap-3 mb-6">
          <Tag className="w-5 h-5 text-[#D4AF37]" />
          <h3 className="font-['Playfair_Display'] text-lg font-bold text-white">Create Promo Code</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div>
            <label className="block text-white/70 text-sm mb-2">Code *</label>
            <Input 
              value={newCode.code} 
              onChange={(e) => setNewCode({...newCode, code: e.target.value.toUpperCase()})} 
              placeholder="SAVE50" 
              className="input-dark"
              data-testid="promo-code-input"
            />
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-2">Type</label>
            <Select value={newCode.discount_type} onValueChange={(v) => setNewCode({...newCode, discount_type: v})}>
              <SelectTrigger className="input-dark" data-testid="promo-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="percentage">%</SelectItem>
                <SelectItem value="fixed">$</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-2">Value *</label>
            <Input 
              type="number" 
              value={newCode.discount_value} 
              onChange={(e) => setNewCode({...newCode, discount_value: e.target.value})} 
              className="input-dark"
              data-testid="promo-value-input"
            />
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-2">Applies To</label>
            <Select value={newCode.applies_to} onValueChange={(v) => setNewCode({...newCode, applies_to: v})}>
              <SelectTrigger className="input-dark" data-testid="promo-applies-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="annual">Annual</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="addons">Add-ons</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-2">Expiration</label>
            <Input 
              type="date" 
              value={newCode.expiration_date} 
              onChange={(e) => setNewCode({...newCode, expiration_date: e.target.value})} 
              className="input-dark"
              data-testid="promo-expiration-input"
            />
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-2">Limit</label>
            <Input 
              type="number" 
              value={newCode.usage_limit} 
              onChange={(e) => setNewCode({...newCode, usage_limit: e.target.value})} 
              placeholder="Unlimited" 
              className="input-dark"
              data-testid="promo-limit-input"
            />
          </div>
        </div>
        <Button onClick={createPromoCode} className="btn-primary mt-4 flex items-center gap-2" data-testid="create-promo-btn">
          <Plus className="w-4 h-4" /> Create
        </Button>
      </div>

      {/* Active Promo Codes */}
      {promoCodes.active?.length > 0 && (
        <div className="card p-6 border border-white/10" data-testid="active-promo-codes">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <Percent className="w-5 h-5 text-green-400" /> Active Codes
            </h3>
            <span className="badge-included">{promoCodes.active.length}</span>
          </div>
          <div className="space-y-2">
            {promoCodes.active.map((code) => (
              <div 
                key={code.id} 
                className="flex items-center justify-between p-3 bg-black/30 border border-green-500/20"
                data-testid={`promo-code-${code.code}`}
              >
                <div className="flex items-center gap-4">
                  <span className="text-[#D4AF37] font-bold">{code.code}</span>
                  <span className="text-white/70">
                    {code.discount_type === "percentage" ? `${code.discount_value}%` : `$${code.discount_value}`}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-white/50 text-sm">Used: {code.usage_count}</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => toggleCode(code.id)} 
                    className="text-yellow-400"
                    data-testid={`toggle-promo-${code.id}`}
                  >
                    <ToggleRight className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => deleteCode(code.id)} 
                    className="text-red-400"
                    data-testid={`delete-promo-${code.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
